import React from 'react';
import { makeStyles } from '@mui/styles';
import { TableRow, TableCell, Chip } from '@mui/material';
import MacronutrientPieChart from '../../MacronutrientPieChart/MacronutrientPieChart';
import { getChipStyles } from '../utils/macronutrientUtils';

const useStyles = makeStyles((theme) => ({
  tableRow: {
    '& td': {
      borderBottom: 'none',
      padding: '8px 16px',
    },
  },
  nonCalorieRow: {
    '& td': {
      '& > div': {
        marginBottom: '30px',
      },
    },
  },
  calorieRow: {
    '& td': {
      paddingTop: '12px',
      paddingBottom: '12px',
      borderBottom: `1px solid ${theme.palette.black12}`,
    },
  },
  firstColumn: {
    wordWrap: 'break-word',
  },
  currentColumn: {
    textAlign: 'left',
  },
  goalColumn: {
    textAlign: 'center',
    '& > div': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  changeColumn: {
    textAlign: 'center',
    fontWeight: 600,
    minWidth: '80px',
    '& > div': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  nutrientName: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    lineHeight: '125%',
    letterSpacing: '0.17px',
  },
  unitRegular: theme.globalClasses.unitRegular,
  unitBold: theme.globalClasses.unitBold,
  chip: {
    height: '34px',
    padding: '8px 12px',
    '& .MuiChip-label': {
      padding: 0,
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '125%',
      letterSpacing: '0.17px',
    },
  },
  pieChartContainer: {
    margin: '0px',
    padding: '0px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  cellContent: {
    display: 'flex',
    alignItems: 'center',
    height: '34px',
    '& > span': {
      lineHeight: '34px',
    },
  },
}));

const NutrientCell = ({ value, unit, percentage, classes }) => {
  if (value === '0') {
    return '';
  }
  return (
    <div className={classes.cellContent}>
      <span className={classes.unitBold}>{value}</span>
      {unit && <span className={classes.unitRegular}>&nbsp;{unit}</span>}
      {percentage !== undefined && <span className={classes.unitRegular}>&nbsp;({percentage}%)</span>}
    </div>
  );
};

const MacronutrientRow = ({ macronutrients, calories, isCalorieRow = false }) => {
  const classes = useStyles();
  const hasGoal = calories ? 'goal' in calories : macronutrients?.some((n) => 'goal' in n);

  if (!macronutrients && !calories) {
    console.warn('MacronutrientRow: No data provided');
    return null;
  }

  if (isCalorieRow && calories) {
    return (
      <TableRow className={`${classes.tableRow} ${classes.calorieRow}`}>
        {hasGoal ? (
          <>
            <TableCell className={classes.firstColumn}>
              <div className={classes.nutrientName}>{calories.name}</div>
            </TableCell>
            <TableCell className={classes.currentColumn}>
              <NutrientCell
                value={calories.current}
                unit={calories.unit}
                classes={classes}
              />
            </TableCell>
            <TableCell>{/* Empty cell for pie chart column */}</TableCell>
            <TableCell className={classes.goalColumn}>
              <NutrientCell
                value={calories.goal}
                unit={calories.unit}
                classes={classes}
              />
            </TableCell>
            <TableCell className={classes.changeColumn}>
              <div className={classes.cellContent}>
                <span className={classes.unitBold}>{calories.calculatedChange}</span>
              </div>
            </TableCell>
          </>
        ) : (
          <>
            <TableCell
              className={classes.firstColumn}
              style={{ width: '50%' }}
            >
              <div className={classes.nutrientName}>{calories.name}</div>
            </TableCell>
            <TableCell
              className={classes.currentColumn}
              style={{ width: '50%', textAlign: 'right' }}
            >
              <NutrientCell
                value={calories.current}
                unit={calories.unit}
                classes={classes}
              />
            </TableCell>
          </>
        )}
      </TableRow>
    );
  }

  const carbohydrateData = macronutrients.find((nutrient) => nutrient.name.toLowerCase() === 'carbohydrates');
  const totalFatData = macronutrients.find((nutrient) => nutrient.name.toLowerCase() === 'total fat');
  const proteinData = macronutrients.find((nutrient) => nutrient.name.toLowerCase() === 'protein');

  return (
    <TableRow className={`${classes.tableRow} ${isCalorieRow ? classes.calorieRow : classes.nonCalorieRow}`}>
      {hasGoal ? (
        <>
          <TableCell className={classes.firstColumn}>
            {macronutrients.map((nutrient, index) => (
              <div
                key={index}
                className={classes.nutrientName}
              >
                <Chip
                  label={nutrient.name}
                  size="small"
                  className={classes.chip}
                  sx={getChipStyles(nutrient.name)}
                />
              </div>
            ))}
          </TableCell>
          <TableCell className={classes.currentColumn}>
            {macronutrients.map((nutrient, index) => (
              <NutrientCell
                key={index}
                value={nutrient.current}
                unit={nutrient.unit}
                percentage={nutrient.currentPercentage}
                classes={classes}
              />
            ))}
          </TableCell>
          <TableCell>
            <div className={classes.pieChartContainer}>
              <MacronutrientPieChart
                carbohydrates={carbohydrateData?.currentPercentage || 0}
                totalFat={totalFatData?.currentPercentage || 0}
                protein={proteinData?.currentPercentage || 0}
              />
            </div>
          </TableCell>
          <TableCell className={classes.goalColumn}>
            {macronutrients.map((nutrient, index) => (
              <NutrientCell
                key={index}
                value={nutrient.goal}
                unit={nutrient.unit}
                percentage={nutrient.goalPercentage}
                classes={classes}
              />
            ))}
          </TableCell>
          <TableCell className={classes.changeColumn}>
            {macronutrients.map((nutrient, index) => (
              <div
                key={index}
                className={classes.cellContent}
              >
                {nutrient.calculatedChange}
              </div>
            ))}
          </TableCell>
        </>
      ) : (
        <>
          <TableCell
            className={classes.firstColumn}
            style={{ width: '50%' }}
          >
            {macronutrients.map((nutrient, index) => (
              <div
                key={index}
                className={classes.nutrientName}
              >
                <Chip
                  label={nutrient.name}
                  size="small"
                  className={classes.chip}
                  sx={getChipStyles(nutrient.name)}
                />
              </div>
            ))}
          </TableCell>
          <TableCell
            className={classes.currentColumn}
            style={{ width: '50%', textAlign: 'right' }}
          >
            {macronutrients.map((nutrient, index) => (
              <NutrientCell
                key={index}
                value={nutrient.current}
                unit={nutrient.unit}
                percentage={nutrient.currentPercentage}
                classes={classes}
              />
            ))}
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

export default MacronutrientRow;
